import { supabase } from './supabase';
import { apiService } from './api';

/**
 * Interface para os parâmetros da requisição
 */
interface RequestParams {
  [key: string]: string | number | boolean;
}

/**
 * Serviço para acessar a API Winthor
 * Usa o apiService para acessar o endpoint /sql/{EMPRESA}/{ENDPOINT}
 */
export const winthorApiService = {
  /**
   * Obtém a empresa do usuário logado no Supabase
   * @returns Código da empresa (ex: 'ABC')
   */
  async obterEmpresaUsuario(): Promise<string> {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      if (!user) {
        throw new Error('Usuário não autenticado');
      }
      
      const { data: userData } = await supabase
        .from('usuarios')
        .select('empresa')
        .eq('id', user.id)
        .single();
      
      if (!userData?.empresa) {
        throw new Error('Empresa do usuário não encontrada');
      }
      
      return userData.empresa;
    } catch (error) {
      console.error('Erro ao obter empresa do usuário:', error);
      throw error;
    }
  },
  
  /**
   * Executa uma consulta SQL na API Winthor usando o apiService
   * @param endpoint Nome do endpoint SQL (ex: 'dash_titulos')
   * @param params Parâmetros da consulta (ex: { codcli: 123 })
   * @param empresa Código da empresa (opcional, será obtido automaticamente se não informado)
   * @returns Resultado da consulta
   */
  async executarConsulta(endpoint: string, params: RequestParams, empresa?: string): Promise<any> {
    try {
      // Se a empresa não foi informada, busca do usuário logado
      const empresaId = empresa || await this.obterEmpresaUsuario();
      
      console.log('Executando consulta SQL:', endpoint);
      console.log('Empresa:', empresaId);
      console.log('Parâmetros:', params);
      
      // Usa o apiService para fazer a requisição
      // O apiService já usa o WORKER_PROXY_URL como base e adiciona o caminho relativo
      // A URL final será: https://worker-proxy.smartbr.workers.dev/sql/{ROTA}/{ENDPOINT}
      // Isso garante que todas as requisições passem pelo worker-proxy para evitar erros de CORS
      const response = await apiService.request(
        'sql',        // Base 'sql' já está definida em API_BASES
        empresaId,    // Código da empresa
        endpoint,     // Nome do endpoint SQL
        'get',        // Método GET para consultas SQL
        params        // Parâmetros da consulta
      );
      
      console.log('Resposta bruta da API:', response);
      
      // Verifica se a resposta tem a estrutura esperada {data: Array}
      if (response && typeof response === 'object' && 'data' in response && Array.isArray(response.data)) {
        console.log('Extraindo array de dados da resposta');
        return response.data; // Retorna apenas o array de dados
      }
      
      // Se não tiver a estrutura esperada, retorna a resposta original
      return response;
    } catch (error) {
      console.error('Erro ao executar consulta SQL:', error);
      throw error;
    }
  },
  
  /**
   * Busca títulos de um cliente
   * @param codCliente Código do cliente
   * @param empresa Código da empresa (opcional)
   * @returns Lista de títulos do cliente
   */
  async buscarTitulosCliente(codCliente: string | number, empresa?: string): Promise<any[]> {
    return this.executarConsulta('dash_titulos', { codcli: codCliente }, empresa);
  },
  
  /**
   * Busca cobranças de um cliente
   * @param codCliente Código do cliente
   * @param empresa Código da empresa (opcional)
   * @returns Lista de cobranças do cliente
   */
  async buscarCobrancasCliente(codCliente: string | number, empresa?: string): Promise<any[]> {
    return this.executarConsulta('dash_cobrancas', { codcli: codCliente }, empresa);
  }
};

export default winthorApiService;
